// src/components/layout/Footer.js
import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link as MuiLink,
  IconButton,
  Divider,
  Stack,
} from '@mui/material';
import {
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  ArrowUpward,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const colors = {
  primary: {
    main: '#2E7D32',
    light: '#4CAF50',
    lighter: '#8BC34A',
    lime: '#CDDC39',
    gradient: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 40%, #8BC34A 70%, #CDDC39 100%)',
  },
  neutral: {
    white: '#FFFFFF',
    mediumGray: '#8D9CA8',
    darkGray: '#4A5B6E',
    charcoal: '#2C3E50',
    dark: '#1A2A3A',
  }
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const socialLinks = [
    { icon: <Facebook />, url: 'https://facebook.com', label: 'Facebook', color: '#1877F2' },
    { icon: <Twitter />, url: 'https://twitter.com', label: 'Twitter', color: '#1DA1F2' },
    { icon: <LinkedIn />, url: 'https://linkedin.com', label: 'LinkedIn', color: '#0A66C2' },
    { icon: <Instagram />, url: 'https://instagram.com', label: 'Instagram', color: '#E4405F' },
  ];

  const footerSections = [
    {
      title: 'Platform',
      links: [
        { text: 'Solutions', path: '/solutions' },
        { text: 'Problems', path: '/problems' },
        { text: 'Organizations', path: '/organizations' },
        { text: 'Pricing', path: '/pricing' },
      ],
    },
    {
      title: 'Company',
      links: [
        { text: 'About', path: '/about' },
        { text: 'Careers', path: '/careers' },
        { text: 'Blog', path: '/blog' },
        { text: 'Contact', path: '/contact' },
      ],
    },
    {
      title: 'Resources',
      links: [
        { text: 'Help Center', path: '/help' },
        { text: 'Documentation', path: '/docs' },
        { text: 'API', path: '/api' },
        { text: 'Status', path: '/status' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { text: 'Privacy', path: '/legal/privacy' },
        { text: 'Terms', path: '/legal/terms' },
        { text: 'Security', path: '/legal/security' },
        { text: 'Cookies', path: '/legal/cookies' },
      ],
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: colors.neutral.dark,
        color: colors.neutral.white,
        pt: 6,
        pb: 3,
        borderTop: `1px solid ${colors.primary.light}`,
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Brand Column */}
          <Grid item xs={12} md={3}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 2,
                background: colors.primary.gradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: '1.5rem',
              }}
            >
              Rengigs
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.neutral.mediumGray,
                mb: 2,
                lineHeight: 1.6,
                maxWidth: 250,
              }}
            >
              Connecting organizations with innovative solutions and expert suppliers across all major industries.
            </Typography>
            <Stack direction="row" spacing={1}>
              {socialLinks.map((social) => (
                <IconButton
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  size="small"
                  sx={{
                    color: colors.neutral.mediumGray,
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: social.color,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Stack>
          </Grid>

          {/* Links Columns */}
          {footerSections.map((section) => (
            <Grid item xs={6} sm={3} md={2} key={section.title}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: colors.primary.lime,
                  letterSpacing: '0.5px',
                }}
              >
                {section.title}
              </Typography>
              <Stack spacing={1}>
                {section.links.map((link) => (
                  <MuiLink
                    key={link.text}
                    component={RouterLink}
                    to={link.path}
                    sx={{
                      color: colors.neutral.mediumGray,
                      textDecoration: 'none',
                      fontSize: '0.85rem',
                      transition: 'color 0.2s, transform 0.2s',
                      display: 'inline-block',
                      '&:hover': {
                        color: colors.primary.lime,
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    {link.text}
                  </MuiLink>
                ))}
              </Stack>
            </Grid>
          ))}

          {/* Brand Message */}
          <Grid item xs={12} md={3}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 2,
                background: colors.primary.gradient,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Rengigs Ecosystem
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.neutral.mediumGray,
                lineHeight: 1.7,
              }}
            >
              Join thousands of companies already using Rengigs to drive innovation and growth.
            </Typography>
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Divider sx={{ my: 3, bgcolor: 'rgba(255,255,255,0.1)' }} />

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap', 
          gap: 2 
        }}>
          <Typography
            variant="caption"
            sx={{ color: colors.neutral.mediumGray }}
          >
            © {currentYear} Rengigs. All rights reserved.
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <MuiLink
              component={RouterLink}
              to="/legal/privacy"
              sx={{
                color: colors.neutral.mediumGray,
                textDecoration: 'none',
                fontSize: '0.75rem',
                '&:hover': { color: colors.primary.lime },
              }}
            >
              Privacy
            </MuiLink>
            <MuiLink
              component={RouterLink}
              to="/legal/terms"
              sx={{
                color: colors.neutral.mediumGray,
                textDecoration: 'none',
                fontSize: '0.75rem',
                '&:hover': { color: colors.primary.lime },
              }}
            >
              Terms
            </MuiLink>
            <MuiLink
              component={RouterLink}
              to="/legal/security"
              sx={{
                color: colors.neutral.mediumGray,
                textDecoration: 'none',
                fontSize: '0.75rem',
                '&:hover': { color: colors.primary.lime },
              }}
            >
              Security
            </MuiLink>
            <MuiLink
              component={RouterLink}
              to="/legal/cookies"
              sx={{
                color: colors.neutral.mediumGray,
                textDecoration: 'none',
                fontSize: '0.75rem',
                '&:hover': { color: colors.primary.lime },
              }}
            >
              Cookies
            </MuiLink>
            <IconButton
              onClick={scrollToTop}
              size="small"
              sx={{
                color: colors.neutral.mediumGray,
                '&:hover': { 
                  color: colors.primary.lime,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <ArrowUpward fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;